import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Formate une date en français
 * @param {string|Date} date - La date à formater
 * @param {string} formatStr - Le format de sortie (par défaut: 'dd/MM/yyyy')
 * @returns {string} - La date formatée
 */
export const formatDate = (date, formatStr = 'dd/MM/yyyy') => {
  if (!date) return '-';
  
  try {
    let dateObj;
    
    if (typeof date === 'string') {
      dateObj = parseISO(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return '-';
    }
    
    if (!isValid(dateObj)) {
      return '-';
    }
    
    return format(dateObj, formatStr, { locale: fr });
  } catch (error) {
    console.error('Erreur lors du formatage de la date:', error);
    return '-';
  }
};

/**
 * Formate une date avec l'heure
 * @param {string|Date} date - La date à formater
 * @returns {string} - La date et heure formatées
 */
export const formatDateTime = (date) => {
  return formatDate(date, 'dd/MM/yyyy à HH:mm');
};

/**
 * Formate un montant en euros
 * @param {number|string} amount - Le montant à formater
 * @param {boolean} showCents - Afficher les centimes (par défaut: true)
 * @returns {string} - Le montant formaté
 */
export const formatCurrency = (amount, showCents = true) => {
  if (amount === null || amount === undefined || amount === '') {
    return '0,00 €';
  }
  
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '0,00 €';
  }
  
  const options = {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0
  };
  
  return new Intl.NumberFormat('fr-FR', options).format(numAmount);
};

/**
 * Formate un nombre avec des séparateurs de milliers
 * @param {number|string} number - Le nombre à formater
 * @returns {string} - Le nombre formaté
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined || number === '') {
    return '0';
  }
  
  const numValue = typeof number === 'string' ? parseFloat(number) : number;
  
  if (isNaN(numValue)) {
    return '0';
  }
  
  return new Intl.NumberFormat('fr-FR').format(numValue);
};

/**
 * Formate un pourcentage
 * @param {number|string} value - La valeur à formater
 * @param {number} decimals - Nombre de décimales (par défaut: 1)
 * @returns {string} - Le pourcentage formaté
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || value === '') {
    return '0%';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0%';
  }
  
  return `${numValue.toFixed(decimals)}%`;
};

/**
 * Formate une durée en heures et minutes
 * @param {number} minutes - Durée en minutes
 * @returns {string} - Durée formatée
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) {
    return '0h';
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes}min`;
  }
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h${remainingMinutes}min`;
};

/**
 * Formate un nom complet
 * @param {string} firstName - Prénom
 * @param {string} lastName - Nom de famille
 * @returns {string} - Nom complet formaté
 */
export const formatFullName = (firstName, lastName) => {
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';
  
  if (!first && !last) {
    return 'Nom non défini';
  }
  
  if (!first) {
    return last;
  }
  
  if (!last) {
    return first;
  }
  
  return `${first} ${last}`;
};

/**
 * Formate une adresse complète
 * @param {Object} address - Objet adresse
 * @returns {string} - Adresse formatée
 */
export const formatAddress = (address) => {
  if (!address) {
    return 'Adresse non définie';
  }
  
  const parts = [];
  
  if (address.street) parts.push(address.street);
  if (address.postCode && address.city) {
    parts.push(`${address.postCode} ${address.city}`);
  } else if (address.city) {
    parts.push(address.city);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Adresse non définie';
};

/**
 * Tronque un texte à une longueur donnée
 * @param {string} text - Texte à tronquer
 * @param {number} maxLength - Longueur maximale
 * @returns {string} - Texte tronqué
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  
  if (text.length <= maxLength) {
    return text;
  }
  
  return text.substring(0, maxLength) + '...';
};

/**
 * Capitalise la première lettre d'un texte
 * @param {string} text - Texte à capitaliser
 * @returns {string} - Texte capitalisé
 */
export const capitalize = (text) => {
  if (!text) return '';
  
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Formate un numéro de téléphone français
 * @param {string} phone - Numéro de téléphone
 * @returns {string} - Numéro formaté
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Supprime tous les caractères non numériques
  const cleaned = phone.replace(/\D/g, '');
  
  // Formate en 01 23 45 67 89
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }
  
  return phone;
};

/**
 * Formate un email en masquant une partie
 * @param {string} email - Email à formater
 * @returns {string} - Email partiellement masqué
 */
export const formatMaskedEmail = (email) => {
  if (!email || !email.includes('@')) {
    return email;
  }
  
  const [localPart, domain] = email.split('@');
  
  if (localPart.length <= 2) {
    return email;
  }
  
  const maskedLocal = localPart.charAt(0) + '*'.repeat(localPart.length - 2) + localPart.charAt(localPart.length - 1);
  
  return `${maskedLocal}@${domain}`;
};
